import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ShieldCheck, AlertTriangle, HelpCircle,
  ChevronLeft, ChevronRight, Search, X,
  MoreHorizontal, Eye, Archive, Trash2, Ban, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL}/api`;
const VERDICT_META = {
  FAKE:      { color: "text-verdict-fake",      icon: AlertTriangle },
  REAL:      { color: "text-verdict-real",      icon: ShieldCheck   },
  UNCERTAIN: { color: "text-verdict-uncertain", icon: HelpCircle    },
};
const STATUS_COLOR = {
  rejected: "text-verdict-fake",
  archived: "text-amber-400",
};

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page - 1)} disabled={page <= 1}><ChevronLeft className="w-3.5 h-3.5" /></Button>
      <span className="text-xs font-mono text-muted-foreground">{page} / {pages}</span>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-sm" onClick={() => onPage(page + 1)} disabled={page >= pages}><ChevronRight className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

function ActionMenu({ analysis, onRefresh }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 176;
    const left = Math.min(
      window.innerWidth - menuWidth - 8,
      Math.max(8, rect.right - menuWidth),
    );
    const top = Math.min(window.innerHeight - 8, rect.bottom + 6);
    setMenuPos({ top, left });
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const handleUpdate = () => updateMenuPosition();
    window.addEventListener("resize", handleUpdate);
    window.addEventListener("scroll", handleUpdate, true);
    return () => {
      window.removeEventListener("resize", handleUpdate);
      window.removeEventListener("scroll", handleUpdate, true);
    };
  }, [open, updateMenuPosition]);

  const updateStatus = async (status) => {
    setOpen(false);
    try {
      await axios.patch(`${API}/admin/analyses/${analysis.id}/status`, { status }, { withCredentials: true });
      toast.success(`Analysis marked as ${status}`);
      onRefresh();
    } catch { toast.error("Failed to update status"); }
  };

  const deleteAnalysis = async () => {
    setOpen(false);
    if (!window.confirm("Permanently delete this analysis? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/analyses/${analysis.id}`, { withCredentials: true });
      toast.success("Analysis deleted");
      onRefresh();
    } catch { toast.error("Failed to delete analysis"); }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          if (!open) updateMenuPosition();
          setOpen((v) => !v);
        }}
        className="w-7 h-7 rounded-sm flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
        data-testid={`analysis-action-${analysis.id?.slice(0,8)}`}>
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
          className="z-[120] w-44 rounded-sm border border-border bg-popover shadow-lg overflow-hidden"
        >
          <Link to={`/results/${analysis.id}`} target="_blank"
            className="flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-secondary/60 transition-colors text-foreground"
            onClick={() => setOpen(false)}>
            <Eye className="w-3.5 h-3.5" /> View Result
          </Link>
          <button onClick={() => updateStatus(analysis.admin_status === "rejected" ? "active" : "rejected")}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-secondary/60 transition-colors text-verdict-fake text-left">
            <Ban className="w-3.5 h-3.5" />
            {analysis.admin_status === "rejected" ? "Restore" : "Reject"}
          </button>
          <button onClick={() => updateStatus(analysis.admin_status === "archived" ? "active" : "archived")}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-secondary/60 transition-colors text-amber-400 text-left">
            <Archive className="w-3.5 h-3.5" />
            {analysis.admin_status === "archived" ? "Unarchive" : "Archive"}
          </button>
          <div className="border-t border-border" />
          <button onClick={deleteAnalysis}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-verdict-fake/10 transition-colors text-verdict-fake text-left">
            <Trash2 className="w-3.5 h-3.5" /> Delete Permanently
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function AdminAnalyses() {
  const { t } = useTranslation();
  const [data, setData] = useState({ analyses: [], total: 0, page: 1, pages: 1 });
  const [verdict, setVerdict] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page };
      if (verdict) params.verdict = verdict;
      if (search.trim()) params.search = search.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/admin/analyses`, { params, withCredentials: true });
      setData(res.data);
    } catch { } finally { setLoading(false); }
  }, [verdict, search, dateFrom, dateTo]);

  // Intentionally refresh on filter/date changes; search runs on submit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [verdict, dateFrom, dateTo]);

  const handleSearch = (e) => { e.preventDefault(); load(1); };
  const clearFilters = () => { setVerdict(""); setSearch(""); setDateFrom(""); setDateTo(""); };
  const hasFilters = verdict || search || dateFrom || dateTo;

  const TH = ["admin.analysesPage.thVideo", "admin.analysesPage.thUser", "admin.analysesPage.thVerdict", "admin.analysesPage.thConf", "admin.analysesPage.thType", "admin.analysesPage.thDate", ""];

  return (
    <div className="p-6 md:p-8" data-testid="admin-analyses-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold">{t("admin.analysesPage.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("admin.analysesPage.totalDesc", { total: data.total })}</p>
      </div>

      {/* Filters — matching AuditLog card style */}
      <div className="mb-6 rounded-md border border-border bg-card p-4" data-testid="analyses-filters">
        <div className="flex flex-wrap items-end gap-3">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.analysesPage.searchPlaceholder")}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("admin.analysesPage.searchPlaceholder")}
                className="w-full bg-background border border-border rounded-sm pl-8 pr-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
                data-testid="analyses-search" />
            </div>
          </form>
          <div className="min-w-[140px]">
            <label className="text-xs font-mono text-muted-foreground mb-1 block">Verdict</label>
            <select value={verdict} onChange={e => setVerdict(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="verdict-filter-select">
              <option value="">{t("admin.analysesPage.all")}</option>
              <option value="FAKE">FAKE</option>
              <option value="REAL">REAL</option>
              <option value="UNCERTAIN">UNCERTAIN</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateFrom")}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="date-from" />
          </div>
          <div>
            <label className="text-xs font-mono text-muted-foreground mb-1 block">{t("admin.audit.dateTo")}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-background border border-border rounded-sm px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-brand-primary"
              data-testid="date-to" />
          </div>
          <div className="flex items-center gap-1.5">
            {hasFilters && (
              <Button size="sm" variant="ghost" className="rounded-sm gap-1 text-xs h-8 text-muted-foreground" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-3 h-3" /> Reset
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-sm gap-1.5 text-xs h-8" onClick={() => load(data.page)} disabled={loading} data-testid="analyses-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-mono text-muted-foreground">
          {t("admin.audit.showing", { count: data.analyses.length, total: data.total })}
        </span>
      </div>

      <div className="rounded-md border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                {TH.map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-mono uppercase tracking-widest text-muted-foreground">{h ? t(h) : ""}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">{t("admin.analysesPage.loading")}</td></tr>
              ) : data.analyses.map(a => {
                const meta = VERDICT_META[a.verdict] || VERDICT_META.UNCERTAIN;
                const VIcon = meta.icon;
                const statusCls = STATUS_COLOR[a.admin_status] || "";
                return (
                  <tr key={a.id} className={`border-b border-border last:border-0 hover:bg-secondary/20 transition-colors ${a.admin_status === "rejected" ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3 max-w-[160px]">
                      <p className="text-sm font-medium truncate" title={a.video_name}>{a.video_name}</p>
                      {a.admin_status && a.admin_status !== "active" && (
                        <span className={`text-[10px] font-mono uppercase ${statusCls}`}>{a.admin_status}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[150px]">
                      <p className="text-xs font-medium truncate">{a.user_name || "\u2014"}</p>
                      <p className="text-xs font-mono text-muted-foreground truncate">{a.user_email || "\u2014"}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`flex items-center gap-1.5 text-xs font-mono font-semibold ${meta.color}`}>
                        <VIcon className="w-3 h-3" />{a.verdict}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">{Math.round(a.confidence || 0)}%</td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground capitalize">{a.video_type}</td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta" }) : "\u2014"}
                    </td>
                    <td className="px-4 py-3">
                      <ActionMenu analysis={a} onRefresh={() => load(data.page)} />
                    </td>
                  </tr>
                );
              })}
              {!loading && data.analyses.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">
                  {hasFilters ? t("admin.analysesPage.noDataFilter") : t("admin.analysesPage.noData")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} pages={data.pages} onPage={load} />
      </div>
    </div>
  );
}
